import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Star,
  BadgeCheck,
  Truck,
  Wallet,
  HeartHandshake,
  ShoppingBag,
  Quote,
  Sparkles,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import Story from "../components/Story";
import {
  heroData,
  whyItems,
  testimonials,
  stats,
  faq,
  formatRupiah,
  formatSold,
} from "../mock";
import { getProducts } from "../api";

const iconMap = { BadgeCheck, Truck, Wallet, HeartHandshake };

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#E3F1EC] relative overflow-hidden">
      <div className="absolute top-40 -left-32 w-96 h-96 bg-[#C5E8DE] rounded-full blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-[600px] -right-32 w-96 h-96 bg-[#D4EEFB] rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute top-[1400px] -left-32 w-96 h-96 bg-[#FFF3D6] rounded-full blur-3xl opacity-60 pointer-events-none" />

      {/* HERO */}
      <section id="hero" className="relative pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-12 flex flex-col justify-center shadow-xl shadow-green-900/5 relative overflow-hidden">
            <div className="absolute top-6 right-8 flex gap-1">
              <Star className="w-4 h-4 text-[#FFD66B] fill-[#FFD66B]" />
              <Star className="w-4 h-4 text-[#FFD66B] fill-[#FFD66B]" />
              <Star className="w-4 h-4 text-[#FFD66B] fill-[#FFD66B]" />
            </div>
            <span className="inline-flex w-fit items-center gap-2 bg-[#FFF1E6] text-[#E8630C] text-xs font-bold px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              {heroData.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#004743] leading-[1.15] tracking-tight">
              {heroData.titleTop}
              <br />
              {heroData.titleBottom}
            </h1>
            <p className="text-[#6B857E] mt-5 text-sm md:text-base leading-relaxed max-w-sm">
              {heroData.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollTo("produk")}
                className="inline-flex items-center gap-2 bg-[#004743] text-white px-8 py-3.5 rounded-full font-bold text-sm hover:bg-[#0A5C53] hover:shadow-lg hover:shadow-green-700/25 hover:-translate-y-0.5 transition-all duration-300"
              >
                {heroData.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-10 flex gap-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="text-2xl font-extrabold text-[#004743]">
                    {s.value}
                  </p>
                  <p className="text-xs text-[#8AA59D] mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative bg-gradient-to-b from-[#BCE3DA] to-[#7CC4B4] rounded-[2.5rem] overflow-hidden shadow-xl shadow-green-900/10 min-h-[380px] group">
            <img
              src={heroData.image}
              alt="ruangrasa Hero"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#004743]/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-md rounded-3xl p-4 flex items-center gap-3 shadow-lg">
              <div className="w-11 h-11 rounded-2xl bg-[#FD7A18] flex items-center justify-center shrink-0">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#004743]">
                  Gratis Ongkir Pulau Jawa
                </p>
                <p className="text-xs text-[#6B857E]">Min. belanja Rp 100.000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="produk" className="relative py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#FFF1E6] text-[#E8630C] text-xs font-bold px-4 py-2 rounded-full shadow-sm mb-4">
              Koleksi Kami
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#004743] tracking-tight">
              Produk Paling Diburu
            </h2>
            <p className="text-[#6B857E] mt-3 text-sm md:text-base max-w-md mx-auto">
              Pilih favoritmu, semua ready stock dan siap dikirim hari ini juga.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-[2rem] p-3 animate-pulse"
                >
                  <div className="rounded-[1.5rem] bg-[#E3F1ED] aspect-square" />
                  <div className="h-4 bg-[#E3F1ED] rounded-full mt-4 w-2/3" />
                  <div className="h-4 bg-[#E3F1ED] rounded-full mt-2 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map((p) => (
                <Link
                  to={`/produk/${p.id}`}
                  key={p.id}
                  className="group bg-white rounded-[2rem] p-3 shadow-lg shadow-green-900/5 hover:shadow-xl hover:shadow-green-900/15 hover:-translate-y-2 transition-all duration-300"
                >
                  <div
                    className="relative rounded-[1.5rem] overflow-hidden aspect-square"
                    style={{ backgroundColor: p.color }}
                  >
                    <img
                      src={p.images?.[0]}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className="absolute top-3 left-3 bg-[#FD7A18] text-[10px] font-bold text-white px-3 py-1.5 rounded-full shadow-md shadow-orange-500/25">
                      {p.badge}
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] font-bold text-[#8AA59D] uppercase tracking-wider">
                      {p.category}
                    </p>
                    <h3 className="font-bold text-[#004743] text-sm md:text-base mt-1 leading-snug line-clamp-1">
                      {p.name}
                    </h3>
                    <div className="flex items-center gap-1 mt-1.5">
                      <Star className="w-3.5 h-3.5 text-[#FFD66B] fill-[#FFD66B]" />
                      <span className="text-xs font-bold text-[#004743]">
                        {p.rating}
                      </span>
                      <span className="text-[10px] text-[#8AA59D]">
                        • {formatSold(p.sold)} terjual
                      </span>
                    </div>
                    <div className="flex items-end justify-between mt-2">
                      <div>
                        {p.oldPrice > 0 && (
                          <p className="text-[10px] text-[#AEC6BE] line-through">
                            {formatRupiah(p.oldPrice)}
                          </p>
                        )}
                        <p className="text-base md:text-lg font-extrabold text-[#0B7C6E]">
                          {formatRupiah(p.price)}
                        </p>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-[#E3F1ED] group-hover:bg-[#FD7A18] flex items-center justify-center transition-colors duration-300">
                        <ArrowRight className="w-4 h-4 text-[#0B7C6E] group-hover:text-white transition-colors duration-300" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WHY US */}
      <section id="kenapa" className="relative py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block bg-[#FFF1E6] text-[#E8630C] text-xs font-bold px-4 py-2 rounded-full shadow-sm mb-4">
                Kenapa Harus Beli Disini?
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#004743] tracking-tight leading-tight">
                Belanja Tenang,
                <br />
                Barang Dijamin Oke
              </h2>
              <p className="text-[#6B857E] mt-4 text-sm md:text-base leading-relaxed max-w-sm">
                Ribuan pelanggan sudah membuktikan. Kami jaga kualitas,
                kecepatan, dan pelayanan di setiap pesanan kamu.
              </p>
              <div className="mt-8 bg-white rounded-[2rem] p-5 shadow-lg shadow-green-900/5 flex items-center gap-4 max-w-sm">
                <div className="flex -space-x-3">
                  {[
                    "https://images.unsplash.com/photo-1769961982389-bb243681421a",
                    "https://images.unsplash.com/photo-1643816831186-b2427a8f9f2d",
                    "https://images.unsplash.com/photo-1600481176431-47ad2ab2745d",
                  ].map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt="customer"
                      className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-3.5 h-3.5 text-[#FFD66B] fill-[#FFD66B]"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-[#6B857E] mt-1">
                    <span className="font-bold text-[#004743]">8.000+</span>{" "}
                    pelanggan puas
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyItems.map((item, i) => {
                const Icon = iconMap[item.icon];
                return (
                  <div
                    key={item.title}
                    className={`bg-white rounded-[2rem] p-6 shadow-lg shadow-green-900/5 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-green-900/10 transition-all duration-300 ${
                      i % 2 === 1 ? "sm:translate-y-6" : ""
                    }`}
                  >
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: item.color }}
                    >
                      <Icon className="w-6 h-6 text-[#004743]" />
                    </div>
                    <h3 className="font-bold text-[#004743] text-base">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#6B857E] mt-2 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Story />

      {/* TESTIMONIALS */}
      <section id="testimoni" className="relative py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#FFF1E6] text-[#E8630C] text-xs font-bold px-4 py-2 rounded-full shadow-sm mb-4">
              Testimoni
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#004743] tracking-tight">
              Kata Mereka yang Sudah Belanja
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className={`bg-white rounded-[2rem] p-6 shadow-lg shadow-green-900/5 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300 relative ${
                  i % 2 === 1 ? "lg:translate-y-6" : ""
                }`}
              >
                <Quote className="w-8 h-8 text-[#FFE3CC] absolute top-5 right-5" />
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-12 h-12 rounded-full border-2 border-[#E3F1ED] object-cover"
                  />
                  <div>
                    <p className="font-bold text-sm text-[#004743]">{t.name}</p>
                    <p className="text-[10px] text-[#8AA59D]">{t.role}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.stars)].map((_, s) => (
                    <Star
                      key={s}
                      className="w-3.5 h-3.5 text-[#FFD66B] fill-[#FFD66B]"
                    />
                  ))}
                </div>
                <p className="text-xs text-[#6B857E] leading-relaxed">
                  "{t.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#FFF1E6] text-[#E8630C] text-xs font-bold px-4 py-2 rounded-full shadow-sm mb-4">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#004743] tracking-tight">
              Pertanyaan yang Sering Ditanya
            </h2>
            <p className="text-[#6B857E] mt-3 text-sm md:text-base">
              Belum menemukan jawabannya? Chat admin kami via WhatsApp, ya!
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faq.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white rounded-[1.5rem] px-6 border-none shadow-lg shadow-green-900/5"
              >
                <AccordionTrigger className="text-left text-sm md:text-base font-bold text-[#004743] hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#6B857E] leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Home;
